/**
 * Authentication Utility Functions
 * Handles user session management
 */

const USER_SESSION_KEY = 'userSession';
const AUTH_TOKEN_KEY = 'authToken';
const LOYALTY_DISCOUNT_KEY = 'loyaltyDiscount';

/**
 * Get user session from localStorage
 * @returns {Object|null} User session data or null
 */
export const getUserSession = () => {
  try {
    const sessionData = localStorage.getItem(USER_SESSION_KEY);
    if (sessionData) {
      return JSON.parse(sessionData);
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Save user session to localStorage
 * @param {Object} userData - User data from login response
 * @param {string} token - Authentication token
 */
export const saveUserSession = (userData, token) => {
  try {
    const sessionData = {
      user: userData,
      token: token,
      loginTime: new Date().toISOString()
    };
    localStorage.setItem(USER_SESSION_KEY, JSON.stringify(sessionData));
    if (token) {
      localStorage.setItem(AUTH_TOKEN_KEY, token);
    }
  } catch (error) {
  }
};

/**
 * Clear user session from localStorage
 */
export const clearUserSession = () => {
  try {
    localStorage.removeItem(USER_SESSION_KEY);
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('loggedInGroupId');
    localStorage.removeItem('availableLoyaltyPoints'); // Clear cashback points cache on logout
    clearLoyaltyDiscount(); // Also clear loyalty discount on logout
    // Clear auto-login session marker so next ltkn re-fetches
    try { sessionStorage.removeItem('lastAutoLoginLtkn'); } catch (e) {}
  } catch (error) {
  }
};

/**
 * Check if user is logged in
 * @returns {boolean} True if user is logged in
 */
export const isUserLoggedIn = () => {
  const session = getUserSession();
  if (session && session.user) {
    return true;
  }
  // Fallback: check old localStorage keys
  const userData = localStorage.getItem('userData');
  return userData !== null;
};

/**
 * Get user full name from session
 * Response structure: { data: {...}, profile: { FirstName: "...", LastName: "..." }, ... }
 * @returns {string} User's full name or empty string
 */
export const getUserFullName = () => {
  const session = getUserSession();
  
  if (session && session.user) {
    const user = session.user;
    // Check if profile object exists with FirstName and LastName
    if (user.profile && typeof user.profile === 'object') {
      const profile = user.profile;
      const firstName = profile.FirstName || profile.firstName || profile.first_name || profile.First_Name || '';
      const lastName = profile.LastName || profile.lastName || profile.last_name || profile.Last_Name || '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) return fullName;
    }
    
    // Check if profile is array
    if (user.profile && Array.isArray(user.profile) && user.profile.length > 0) {
      const profile = user.profile[0];
      const firstName = profile.FirstName || profile.firstName || profile.first_name || profile.First_Name || '';
      const lastName = profile.LastName || profile.lastName || profile.last_name || profile.Last_Name || '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) return fullName;
    }
    
    // Check direct properties
    const firstName = user.FirstName || user.firstName || user.first_name || user.First_Name || '';
    const lastName = user.LastName || user.lastName || user.last_name || user.Last_Name || '';
    return `${firstName} ${lastName}`.trim();
  }
  
  // Fallback: check old localStorage format
  try {
    const userDataStr = localStorage.getItem('userData');
    if (userDataStr) {
      const userData = JSON.parse(userDataStr);
      if (userData.profile) {
        const profile = userData.profile;
        const firstName = profile.FirstName || profile.firstName || profile.first_name || '';
        const lastName = profile.LastName || profile.lastName || profile.last_name || '';
        return `${firstName} ${lastName}`.trim();
      }
    }
  } catch (error) {
  }
  
  return '';
};

/**
 * Get authentication token
 * @returns {string|null} Authentication token or null
 */
export const getAuthToken = () => {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem('authToken');
  } catch (error) {
    return null;
  }
};

/**
 * CRM API Base URL
 */
const CRM_API_BASE_URL = 'https://crm.bookingwhizz.com/api';

/**
 * Register user via CRM API
 * @param {Object} registrationData - Registration data
 * @param {string} registrationData.firstname - First name
 * @param {string} registrationData.lastname - Last name
 * @param {string} registrationData.email - Email address
 * @param {string} registrationData.password - Password
 * @param {string} registrationData.confirmpassword - Password confirmation
 * @param {string} registrationData.telephone - Phone number (optional)
 * @param {string} registrationData.countrycode - Country code (optional)
 * @param {string} registrationData.mobilecode - Mobile country code (optional)
 * @param {string} registrationData.hotel_id - Hotel ID
 * @param {string} registrationData.AccessKey - Access Key from accommodationGroupDetail
 * @param {string} registrationData.currentUrl - Current page URL for redirect
 * @returns {Promise<Object>} Registration response
 */
export const registerUser = async (registrationData) => {
  try {
    const {
      firstname,
      lastname,
      email,
      password,
      confirmpassword,
      telephone = '',
      countrycode = '',
      mobilecode = '',
      hotel_id,
      AccessKey,
      currentUrl = window.location.href
    } = registrationData;

    // Validate required fields
    if (!firstname || !lastname || !email || !password || !confirmpassword) {
      throw new Error('First name, last name, email, password, and password confirmation are required');
    }

    if (password !== confirmpassword) {
      throw new Error('Password and confirmation password do not match');
    }

    if (!hotel_id || !AccessKey) {
      throw new Error('Hotel ID and Access Key are required');
    }

    // Prepare data according to PHP API format
    const data = {
      'slp-reg-firstName': firstname.trim(),
      'slp-reg-lastName': lastname.trim(),
      'slp-reg-email': email.trim(),
      'password': password,
      'password_confirmation': confirmpassword,
      'sourceId': 56,
      'accKey': `${hotel_id}-${AccessKey}`,
      'redirectUrl': currentUrl,
      'slp-reg-phoneNumber': telephone.trim() || '',
      'slpRegCountry': countrycode || '',
      'slpRegCountryCode': mobilecode || ''
    };

    // Make API call
    const response = await fetch(`${CRM_API_BASE_URL}/loyalty/register-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `Registration failed: ${response.statusText}`);
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    if (error.message) {
      throw error;
    }
    throw new Error('An error occurred during registration. Please try again.');
  }
};

/**
 * Save loyalty discount data to localStorage
 * @param {Object} loyaltyData - Loyalty discount data
 * @param {number} loyaltyData.Value - Discount percentage value
 * @param {number} loyaltyData.MaxValue - Maximum discount percentage allowed
 * @param {string} loyaltyData.Type - Discount type (e.g., "percent")
 * @param {Object} loyaltyData.membership_type - Membership type object
 * @param {string} loyaltyData.membership_type.MemberShipTypeName - Membership type name
 */
export const saveLoyaltyDiscount = (loyaltyData) => {
  try {
    if (loyaltyData && loyaltyData.success) {
      localStorage.setItem(LOYALTY_DISCOUNT_KEY, JSON.stringify(loyaltyData));
    }
  } catch (error) {

  }
};

/**
 * Get loyalty discount data from localStorage
 * @returns {Object|null} Loyalty discount data or null
 */
export const getLoyaltyDiscount = () => {
  try {
    const loyaltyData = localStorage.getItem(LOYALTY_DISCOUNT_KEY);
    if (loyaltyData) {
      return JSON.parse(loyaltyData);
    }
    return null;
  } catch (error) {

    return null;
  }
};

/**
 * Clear loyalty discount data from localStorage
 */
export const clearLoyaltyDiscount = () => {
  try {
    localStorage.removeItem(LOYALTY_DISCOUNT_KEY);
  } catch (error) {

  }
};

